/**
 * Bloks product media marquee — slow leftward loop, drag, fullscreen expand.
 * Syncs with color swatch hover/click via product select + media preview.
 *
 * Images are fetched once via Image(), painted as CSS backgrounds on slides,
 * then the loop set is cloned from those backgrounds (no second <img> decode).
 * Transform wrapping uses the dual-set width — DOM nodes are not recycled.
 */
import { StandardEvents } from '@shopify/events';

const DRAG_THRESHOLD = 6;

class BloksProductMediaMarquee extends HTMLElement {
  /** @type {number} */
  #offset = 0;

  /** @type {number} */
  #speed = 0;

  /** @type {number} */
  #rafId = 0;

  /** @type {number} */
  #lastTimestamp = 0;

  /** @type {boolean} */
  #isDragging = false;

  /** @type {number} */
  #dragStartX = 0;

  /** @type {number} */
  #dragStartOffset = 0;

  /** @type {number} */
  #dragDistance = 0;

  /** @type {boolean} */
  #reducedMotion = false;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MediaQueryList | null} */
  #motionQuery = null;

  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  /** @type {AbortController} */
  #controller = new AbortController();

  /** @type {HTMLElement | null} */
  #picker = null;

  /** @type {boolean} */
  #isPreviewing = false;

  /** @type {string | null} */
  #selectedMediaId = null;

  /** @type {HTMLImageElement | null} */
  #previewImage = null;

  /** @type {HTMLElement | null} */
  #previewSlide = null;

  /** @type {string | null} */
  #previewOriginalSrc = null;

  /** @type {string | null} */
  #previewOriginalSrcset = null;

  /** @type {string} */
  #previewOriginalBackground = '';

  /** @type {number} */
  #previewRestoreOffset = 0;

  connectedCallback() {
    this.stage = this.querySelector('[data-pmm-stage]');
    this.viewport = this.querySelector('[data-pmm-viewport]');
    this.track = this.querySelector('[data-pmm-track]');
    this.set = this.querySelector('[data-pmm-set]');
    this.dialog = this.querySelector('[data-pmm-dialog]');
    this.closeButton = this.querySelector('[data-pmm-close]');
    this.shell = this.closest('[data-pmm-shell]') || this.parentElement;
    this.placeholder = this.shell?.querySelector('[data-pmm-placeholder]') || null;

    if (!this.stage || !this.viewport || !this.track || !this.set) return;

    const slideCount = this.set.querySelectorAll('[data-pmm-slide]').length;
    if (slideCount === 0) return;

    this.isSingle = this.hasAttribute('data-single') || slideCount <= 1;
    this.canLoop = this.hasAttribute('data-loop') && !this.isSingle;

    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#motionQuery.addEventListener('change', this.#onMotionChange);

    const { signal } = this.#controller;
    const section = this.closest('.shopify-section, dialog');

    section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    this.#picker =
      section?.querySelector('variant-picker[data-template-product-match="true"]') ||
      section?.querySelector('variant-picker') ||
      null;

    this.#selectedMediaId = this.#getSelectedMediaId();

    this.#picker?.addEventListener('pointerover', this.#onPickerPointerOver, { signal });
    this.#picker?.addEventListener('pointerout', this.#onPickerPointerOut, { signal });

    // Single image: no drag/marquee — click/keyboard still open fullscreen
    if (!this.isSingle) {
      this.viewport.addEventListener('pointerdown', this.#onPointerDown);
      this.viewport.addEventListener('pointermove', this.#onPointerMove, { passive: false });
      this.viewport.addEventListener('pointerup', this.#onPointerUp);
      this.viewport.addEventListener('pointercancel', this.#onPointerUp);
    }
    this.viewport.addEventListener('click', this.#onOpenClick);
    this.viewport.addEventListener('keydown', this.#onKeyDown);

    this.closeButton?.addEventListener('click', this.#onCloseClick);
    this.dialog?.addEventListener('cancel', this.#onDialogCancel);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);

    if (this.isSingle) return;

    if (typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(() => {
        this.#updateSpeed();
        this.#offset = this.#normalizeOffset(this.#offset);
        this.#applyTransform();
      });
      this.#resizeObserver.observe(this.track);
      if (this.viewport) this.#resizeObserver.observe(this.viewport);
    }

    requestAnimationFrame(() => {
      this.#paintSlides().then(() => {
        this.#buildLoopSet();
        this.#startAnimation();

        if (this.#selectedMediaId) {
          this.#scrollMediaIntoView(this.#selectedMediaId, { animate: false });
        }
      });
    });
  }

  disconnectedCallback() {
    this.#stopAnimation();
    this.#controller.abort();
    this.#controller = new AbortController();
    this.#clearTemporaryPreview();

    this.viewport?.removeEventListener('pointerdown', this.#onPointerDown);
    this.viewport?.removeEventListener('pointermove', this.#onPointerMove);
    this.viewport?.removeEventListener('pointerup', this.#onPointerUp);
    this.viewport?.removeEventListener('pointercancel', this.#onPointerUp);
    this.viewport?.removeEventListener('click', this.#onOpenClick);
    this.viewport?.removeEventListener('keydown', this.#onKeyDown);
    this.closeButton?.removeEventListener('click', this.#onCloseClick);
    this.dialog?.removeEventListener('cancel', this.#onDialogCancel);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    this.#motionQuery?.removeEventListener('change', this.#onMotionChange);
    this.#resizeObserver?.disconnect();

    if (this.isExpanded) {
      this.#restoreStage();
      if (this.dialog?.open) this.dialog.close();
    }
  }

  get isExpanded() {
    return this.classList.contains('is-expanded');
  }

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const newShell = detail.html.querySelector('[data-pmm-shell]');
        if (!newShell || !this.shell) return;

        this.shell.replaceWith(newShell);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[bloks-product-media-marquee] Event promise rejected:', error);
        }
      });
  };

  /** @returns {string | null} */
  #getSelectedMediaId() {
    const checked = this.#picker?.querySelector(
      '.variant-option--swatches input:checked[data-option-media-id]'
    );
    if (checked instanceof HTMLElement && checked.dataset.optionMediaId) {
      return checked.dataset.optionMediaId;
    }

    const firstSlide = this.set?.querySelector('[data-pmm-slide][data-media-id]');
    return firstSlide instanceof HTMLElement ? firstSlide.dataset.mediaId || null : null;
  }

  /** @param {PointerEvent} event */
  #onPickerPointerOver = (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    if (!(event.target instanceof Element)) return;

    const label = event.target.closest('.variant-option__button-label--has-swatch');
    if (!label || !this.#picker?.contains(label)) return;

    const input = label.querySelector('input[data-option-media-id], input[data-option-media-url]');
    if (!(input instanceof HTMLElement)) return;

    this.previewMedia(input.dataset.optionMediaId, input.dataset.optionMediaUrl);
  };

  /** @param {PointerEvent} event */
  #onPickerPointerOut = (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    if (!(event.target instanceof Element)) return;

    const fieldset = event.target.closest('.variant-option--swatches');
    if (!fieldset || !this.#picker?.contains(fieldset)) return;

    const related = event.relatedTarget;
    if (related instanceof Node && fieldset.contains(related)) return;

    this.resetPreview();
  };

  /**
   * @param {string | undefined | null} mediaId
   * @param {string | undefined | null} mediaUrl
   */
  previewMedia(mediaId, mediaUrl) {
    if (!mediaId && !mediaUrl) return;

    if (!this.#isPreviewing) {
      this.#previewRestoreOffset = this.#offset;
    }

    this.#isPreviewing = true;

    if (mediaId && this.#scrollMediaIntoView(mediaId, { animate: false })) {
      this.#clearTemporaryPreview();
      return;
    }

    if (mediaUrl) {
      this.#applyTemporaryPreview(mediaUrl);
      this.#scrollToFirstSlide();
    }
  }

  resetPreview() {
    if (!this.#isPreviewing) return;

    this.#isPreviewing = false;
    this.#clearTemporaryPreview();

    this.#selectedMediaId = this.#getSelectedMediaId();

    if (this.#selectedMediaId && this.#scrollMediaIntoView(this.#selectedMediaId, { animate: false })) {
      return;
    }

    this.#offset = this.#normalizeOffset(this.#previewRestoreOffset);
    this.#applyTransform();
  }

  /**
   * @param {string} mediaId
   * @param {{ animate?: boolean }} [options]
   * @returns {boolean}
   */
  #scrollMediaIntoView(mediaId, options = {}) {
    if (!this.set || !this.viewport || !mediaId) return false;

    const slide = this.set.querySelector(`[data-pmm-slide][data-media-id="${CSS.escape(mediaId)}"]`);
    if (!(slide instanceof HTMLElement)) return false;

    const viewportRect = this.viewport.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    this.#offset = this.#normalizeOffset(this.#offset - (slideRect.left - viewportRect.left));
    this.#applyTransform();

    if (options.animate === false) {
      /* transform applied immediately */
    }

    return true;
  }

  #scrollToFirstSlide() {
    const firstSlide = this.set?.querySelector('[data-pmm-slide]');
    if (!(firstSlide instanceof HTMLElement) || !this.viewport) return;

    const viewportRect = this.viewport.getBoundingClientRect();
    const slideRect = firstSlide.getBoundingClientRect();
    this.#offset = this.#normalizeOffset(this.#offset - (slideRect.left - viewportRect.left));
    this.#applyTransform();
  }

  /** @param {string} url */
  #applyTemporaryPreview(url) {
    const slide = this.set?.querySelector('[data-pmm-slide]');
    const image = slide?.querySelector('img');
    if (!(slide instanceof HTMLElement) || !(image instanceof HTMLImageElement)) return;

    if (!this.#previewImage) {
      this.#previewImage = image;
      this.#previewSlide = slide;
      this.#previewOriginalSrc = image.getAttribute('src');
      this.#previewOriginalSrcset = image.getAttribute('srcset');
      this.#previewOriginalBackground = slide.style.backgroundImage;
    }

    image.src = url;
    image.removeAttribute('srcset');
    slide.style.backgroundImage = `url(${JSON.stringify(url)})`;
  }

  #clearTemporaryPreview() {
    if (!this.#previewImage) return;

    if (this.#previewOriginalSrc != null) {
      this.#previewImage.setAttribute('src', this.#previewOriginalSrc);
    }
    if (this.#previewOriginalSrcset != null) {
      this.#previewImage.setAttribute('srcset', this.#previewOriginalSrcset);
    } else {
      this.#previewImage.removeAttribute('srcset');
    }

    if (this.#previewSlide) {
      this.#previewSlide.style.backgroundImage = this.#previewOriginalBackground;
    }

    this.#previewImage = null;
    this.#previewSlide = null;
    this.#previewOriginalSrc = null;
    this.#previewOriginalSrcset = null;
    this.#previewOriginalBackground = '';
  }

  /** @param {MediaQueryListEvent} event */
  #onMotionChange = (event) => {
    this.#reducedMotion = event.matches;
  };

  /**
   * Fetch every slide URL once (outside overflow clipping), paint as background.
   * @returns {Promise<void>}
   */
  async #paintSlides() {
    if (!this.set || !this.viewport) return;

    const slides = [...this.set.querySelectorAll('[data-pmm-slide]')];
    if (!slides.length) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const displayWidth =
      (slides[0] instanceof HTMLElement && slides[0].offsetWidth) ||
      this.viewport.clientHeight * 0.75 ||
      480;
    const targetWidth = Math.ceil(displayWidth * dpr);

    await Promise.all(
      slides.map(async (slide) => {
        if (!(slide instanceof HTMLElement)) return;

        const img = slide.querySelector('img');
        if (!(img instanceof HTMLImageElement)) return;

        const srcset = img.getAttribute('srcset') || '';
        const url =
          this.#bestSrcsetUrl(srcset, targetWidth) || img.currentSrc || img.getAttribute('src');
        if (!url) return;

        await this.#loadUrl(url);

        slide.style.backgroundImage = `url(${JSON.stringify(url)})`;
        slide.classList.add('is-ready');

        // Keep <img> in sync for a11y; visual paint is the background
        img.loading = 'eager';
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        if (img.src !== url) img.src = url;
      })
    );
  }

  /**
   * Clone painted slides as background-only nodes for seamless wrapping.
   * No extra <img> elements — reuses the already-decoded URLs.
   */
  #buildLoopSet() {
    if (!this.canLoop || !this.set || !this.track) return;

    this.track.querySelector('[data-pmm-loop-set]')?.remove();

    const loopSet = document.createElement('div');
    loopSet.className = 'bloks-pmm__set';
    loopSet.setAttribute('data-pmm-loop-set', '');
    loopSet.setAttribute('aria-hidden', 'true');

    this.set.querySelectorAll('[data-pmm-slide]').forEach((slide) => {
      if (!(slide instanceof HTMLElement)) return;

      const clone = document.createElement('figure');
      clone.className = 'bloks-pmm__slide is-ready';
      clone.style.backgroundImage = slide.style.backgroundImage;
      loopSet.appendChild(clone);
    });

    this.track.appendChild(loopSet);
  }

  /**
   * @param {string} url
   * @returns {Promise<string>}
   */
  #loadUrl(url) {
    return new Promise((resolve) => {
      const image = new Image();
      image.decoding = 'async';
      image.onload = () => resolve(url);
      image.onerror = () => resolve(url);
      image.src = url;
    });
  }

  /**
   * @param {string | null | undefined} srcset
   * @param {number} targetWidth
   * @returns {string | null}
   */
  #bestSrcsetUrl(srcset, targetWidth) {
    if (!srcset) return null;

    const candidates = srcset
      .split(',')
      .map((part) => {
        const bits = part.trim().split(/\s+/);
        return { url: bits[0], width: parseInt(bits[1], 10) || 0 };
      })
      .filter((candidate) => candidate.url && candidate.width > 0)
      .sort((a, b) => a.width - b.width);

    if (!candidates.length) return null;

    return (
      candidates.find((candidate) => candidate.width >= targetWidth)?.url ||
      candidates[candidates.length - 1]?.url ||
      null
    );
  }

  #getLoopWidth() {
    if (!this.set) return 0;
    const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    return this.set.getBoundingClientRect().width + gap;
  }

  /** @param {number} value */
  #normalizeOffset(value) {
    if (!this.canLoop) return value;

    const loopWidth = this.#getLoopWidth();
    if (loopWidth <= 0) return value;

    value = value % loopWidth;
    if (value > 0) value -= loopWidth;
    return value;
  }

  #applyTransform() {
    if (!this.track) return;
    this.track.style.transform = `translate3d(${this.#offset}px, 0, 0)`;
  }

  #updateSpeed() {
    const durationValue = getComputedStyle(this).getPropertyValue('--bloks-pmm-duration').trim();
    const duration = parseFloat(durationValue) || 48;
    const viewportWidth = this.viewport?.clientWidth || 0;
    // Speed = referenceWidth / duration keeps px/s independent of image count.
    // On mobile the gallery is much narrower, so viewport-only refs crawled.
    // Floor at a desktop-column width so mobile feels as fast as desktop.
    const DESKTOP_GALLERY_REF_PX = 900;
    const referenceWidth = Math.max(viewportWidth, DESKTOP_GALLERY_REF_PX);
    this.#speed = referenceWidth > 0 && duration > 0 ? referenceWidth / duration : 0;
  }

  /** @param {number} timestamp */
  #tick = (timestamp) => {
    if (!this.#lastTimestamp) this.#lastTimestamp = timestamp;
    const delta = (timestamp - this.#lastTimestamp) / 1000;
    this.#lastTimestamp = timestamp;

    if (
      this.canLoop &&
      !this.#isDragging &&
      !this.#isPreviewing &&
      !this.#reducedMotion &&
      this.#speed > 0 &&
      !document.hidden
    ) {
      this.#offset -= this.#speed * delta;
      this.#offset = this.#normalizeOffset(this.#offset);
      this.#applyTransform();
    }

    this.#rafId = window.requestAnimationFrame(this.#tick);
  };

  #stopAnimation() {
    if (this.#rafId) {
      window.cancelAnimationFrame(this.#rafId);
      this.#rafId = 0;
      this.#lastTimestamp = 0;
    }
  }

  #startAnimation() {
    this.#stopAnimation();
    this.#updateSpeed();
    this.#offset = this.#normalizeOffset(this.#offset);
    this.#applyTransform();
    this.#rafId = window.requestAnimationFrame(this.#tick);
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) return;

    this.#isDragging = true;
    this.#dragStartX = event.clientX;
    this.#dragStartOffset = this.#offset;
    this.#dragDistance = 0;
    this.viewport?.setAttribute('data-dragging', '');

    if (this.viewport?.setPointerCapture) {
      this.viewport.setPointerCapture(event.pointerId);
    }
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (!this.#isDragging) return;

    const delta = event.clientX - this.#dragStartX;
    this.#dragDistance = Math.max(this.#dragDistance, Math.abs(delta));

    if (this.#dragDistance >= DRAG_THRESHOLD) {
      event.preventDefault();
    }

    this.#offset = this.#dragStartOffset + delta;
    this.#applyTransform();
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    if (!this.#isDragging) return;

    this.#isDragging = false;
    this.viewport?.removeAttribute('data-dragging');
    this.#offset = this.#normalizeOffset(this.#offset);
    this.#applyTransform();

    if (this.viewport?.releasePointerCapture) {
      try {
        this.viewport.releasePointerCapture(event.pointerId);
      } catch {
        /* already released */
      }
    }
  };

  /** @param {MouseEvent} event */
  #onOpenClick = (event) => {
    if (this.#dragDistance >= DRAG_THRESHOLD) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    event.preventDefault();

    if (this.isExpanded) {
      this.closeFullscreen();
      return;
    }

    this.openFullscreen();
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (this.isExpanded) {
        this.closeFullscreen();
      } else {
        this.openFullscreen();
      }
    }
  };

  #onCloseClick = () => {
    this.closeFullscreen();
  };

  /** @param {Event} event */
  #onDialogCancel = (event) => {
    event.preventDefault();
    this.closeFullscreen();
  };

  #onVisibilityChange = () => {
    if (document.hidden) {
      this.#lastTimestamp = 0;
    }
  };

  #restoreStage() {
    if (!this.stage || !this.dialog) return;
    if (this.stage.parentElement === this.dialog) {
      this.insertBefore(this.stage, this.dialog);
    }
  }

  openFullscreen() {
    if (!this.dialog || !this.stage || this.isExpanded) return;

    this.#previouslyFocused = /** @type {HTMLElement | null} */ (document.activeElement);

    const height = this.getBoundingClientRect().height;
    if (this.placeholder) {
      this.placeholder.style.height = `${Math.max(height, 1)}px`;
      this.placeholder.hidden = false;
    }

    this.dialog.appendChild(this.stage);
    this.classList.add('is-expanded');
    this.setAttribute('aria-expanded', 'true');
    this.dialog.showModal();

    requestAnimationFrame(() => {
      this.#updateSpeed();
      this.#offset = this.#normalizeOffset(this.#offset);
      this.#applyTransform();
      this.closeButton?.focus();
    });
  }

  closeFullscreen() {
    if (!this.dialog || !this.isExpanded) return;

    this.#restoreStage();
    this.classList.remove('is-expanded');
    this.setAttribute('aria-expanded', 'false');

    if (this.dialog.open) {
      this.dialog.close();
    }

    if (this.placeholder) {
      this.placeholder.hidden = true;
      this.placeholder.style.height = '';
    }

    requestAnimationFrame(() => {
      this.#updateSpeed();
      this.#offset = this.#normalizeOffset(this.#offset);
      this.#applyTransform();
    });

    const focusTarget = this.#previouslyFocused || this.viewport;
    focusTarget?.focus?.();
    this.#previouslyFocused = null;
  }
}

if (!customElements.get('bloks-product-media-marquee')) {
  customElements.define('bloks-product-media-marquee', BloksProductMediaMarquee);
}
